import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';

import { Link } from 'react-router-dom';

import { connect } from 'react-redux';

import first from 'lodash/fp/first';
import map from 'lodash/fp/map';
import noop from 'lodash/fp/noop';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import ExtendableTextBlock from 'Components/ExtendableTextBlock';
import FragranceDescription from 'Components/FragranceDescription';
import { Button } from 'Components/LegacyButton';
import QuantitySelector from 'Components/QuantitySelector';
import RadioButton from 'Components/RadioButton';
import { SelectField } from 'Components/SelectField';
import Spacer from 'Components/Spacer';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { productsCategories, productsSlugs, skincareProducts } from 'constants/products';
import { getFrequencyLabelForValue } from 'constants/subscriptions';

import MonogramEditor from '../Containers/MonogramEditor';

import checkoutCategoryProp from 'PropTypes/checkoutProps';
import consultationCountriesProps from 'PropTypes/countries';

import { accessoriesProducts, brush } from 'assets/content/accessoriesProducts';
import { clinicalClaimsContent } from 'assets/content/checkout';
import { ReactComponent as MembershipQuestionMark } from 'assets/images/active-question-lime.svg';
import { ReactComponent as Cross } from 'assets/images/cross_icon.svg';

import { formatPriceWithCurrency } from 'utils/currencies';
import { genitive } from 'utils/textUtils';
import useMedia from 'utils/useMedia';

import * as checkoutCartActions from 'dux/checkoutCart/actions';
import { updateMonogram as updateMonogramAction } from 'dux/checkoutCart/actions';
import {
  getCurrencyFromCart,
  getEnableSubscriptionAtCart,
  getMustHaveJarsInCart,
} from 'dux/checkoutCart/selectors';
import { getProductImageForProduct } from 'dux/products/selectors';
import * as subscriptionSelectors from 'dux/subscriptions/selectors';
import * as userSelectors from 'dux/user/selectors';

const Root = styled.div`
  position: relative;

  display: grid;
  align-items: center;
  ${legacyTheme.breakpoints.up('sm')} {
    align-items: flex-start;
  }
  grid-template-columns: var(--image-column-width) var(--text-column-width);
  row-gap: ${legacyTheme.spacing.s8};
  column-gap: var(--card-grid-column-gap);

  grid-template-areas:
    'image mainInfo '
    'image collapses'
    'image purchaseInfo';

  margin: ${legacyTheme.spacing.s24};
  margin-bottom: 0;
  padding: ${legacyTheme.spacing.s32} var(--card-horizontal-padding);
  background: ${theme.colors.neutral[100]};

  ${legacyTheme.breakpoints.match('md')} {
    width: 100%;
    grid-template-areas:
      'image        mainInfo '
      'collapses    collapses'
      'purchaseInfo purchaseInfo';
  }

  &:last-child {
    margin-bottom: ${legacyTheme.spacing.s24};
  }

  &:before {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    box-shadow: 2px 2px 3px 0 ${theme.colors.neutral[600]};
    mix-blend-mode: multiply;

    content: '';
    pointer-events: none;
  }
`;

const Image = styled.img`
  grid-area: image;

  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const MainInfoWrapper = styled.div`
  display: flex;
  grid-area: mainInfo;

  opacity: ${props => (props.disabled ? 0.5 : 1)};

  ${legacyTheme.breakpoints.match('md')} {
    display: block;
  }
`;

const DescriptionDetailsContainer = styled.span`
  margin: 28px 0px;
`;

const MainInfo = styled.div`
  flex-grow: 1;
  margin-bottom: 14px;
`;

const CollapsedInfo = styled.div`
  grid-area: collapses;

  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const Divider = styled.hr`
  width: 100%;
  margin-top: ${legacyTheme.spacing.s24};
  margin-bottom: ${legacyTheme.spacing.s24};

  border: none;
  border-top: 1px solid ${theme.colors.neutral[600]};

  &:first-of-type {
    margin-top: revert;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CollapsedContent = styled(Typography)`
  padding-top: 8px;
  padding-bottom: 14px;
`;

const Capacity = styled(Typography)`
  margin: 0;
`;

const Text = styled.span`
  color: ${theme.colors.neutral[800]};
`;

const QuestionMarkButton = styled.div`
  position: relative;
  top: 3px;
  left: 6px;

  width: 22;
  height: auto;
  padding: 0;

  border: none;
  background-color: transparent;

  font: inherit;
  text-align: left;
  color: inherit;
  cursor: pointer;
`;

const PurchaseDetails = styled.div`
  grid-area: purchaseInfo;

  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const Selectors = styled.div`
  display: flex;

  // Owl selector. Enables to add a gap between children and avoid gap at the edges. Low specificity
  & > * + * {
    margin-left: 18px;
  }
`;

const QuantitySelectorWrapper = styled.div`
  flex-grow: 1;

  & select {
    font-size: 12px;

    ${legacyTheme.breakpoints.up('md')} {
      font-size: 16px;
    }
  }
`;

const FrequencySelector = styled.div`
  flex-grow: 8;

  & select {
    font-size: 12px;

    ${legacyTheme.breakpoints.up('md')} {
      font-size: 16px;
    }
  }
`;

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceSelector = styled.div`
  display: flex;
`;

const PriceText = styled(Typography)`
  line-height: 28px;
`;

const SubscriptionDiscountWrapper = styled(Typography)`
  display: flex;
  flex-direction: column;

  line-height: 28px;

  ${legacyTheme.breakpoints.up('xs')} {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const SubscriptionDiscount = styled.div`
  display: flex;
  margin-top: -5px;

  ${legacyTheme.breakpoints.up('xs')} {
    margin-top: 0;
    margin-left: 5px;
  }
`;

const SubscriptionLink = styled.div`
  display: flex;
  justify-content: center;
`;

const CloseCardButton = styled.button`
  position: absolute;
  top: ${legacyTheme.spacing.s24};
  right: ${legacyTheme.spacing.s24};

  width: ${legacyTheme.spacing.s24};
  height: ${legacyTheme.spacing.s24};
  margin: 0;
  padding: 0;

  border: none;
  background: none;
  appearance: none;

  cursor: pointer;

  ${legacyTheme.breakpoints.match('md')} {
    right: ${legacyTheme.spacing.s12};
    top: ${legacyTheme.spacing.s12};
  }
`;

const AccessoriesDescriptionContainer = styled.div`
  grid-area: collapses;

  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const PriceNextToQuantity = styled(Typography)`
  // Hack to horizontally align with the select element
  padding-top: 20px;
`;

const ModalLabel = styled(Typography)`
  // Necessary otherwise text looks cut on the upper part on safari
  padding-top: 0.2em;
  line-height: 0.8em;
`;

const useStyles = makeStyles(() => ({
  modalLabelButton: {
    margin: 0,

    textTransform: 'initial',

    '&:after': {
      height: '1px',
    },
  },
}));

const ProductCardCheckout = ({
  checkoutCategory,
  userFirstName,
  fromGiftSet,
  updateItemQuantity,
  addToCartAndDefaultToSubscribeWhenPossible,
  updateItemSubscription,
  product,
  getProductImage,
  onModalOpen,
  hasActiveHaircareSubscription,
  hasActiveSkincareSubscription,
  hasActiveHairSupplementsSubscription,
  subscribableProducts,
  disabled,
  defaultIsOpenedExtendableSection,
  mustHaveJarsInCart,
  updateMonogram,
  currencyFromCart,
  enableSubscriptionAtCart,
  hasFirstNameInLabel,
  consultationProfileCountry,
}) => {
  const isSmallMobile = useMedia(`(max-width: ${breakpoints.xs - 1}px)`);
  const { classes } = useStyles();

  const {
    capacity,
    category,
    customization,
    description,
    description_details,
    immutable,
    ingredients,
    label,
    max_quantity: maxQuantity,
    min_quantity: minQuantity,
    product: productType,
    quantity,
    routine,
    subscription,
    type,
    type: slug,
    unit_price: price,
  } = product;
  const monograms = customization && customization.monograms;
  const show_add_to_my_subscription =
    ((hasActiveHaircareSubscription && checkoutCategory === productsCategories.HAIRCARE) ||
      (hasActiveSkincareSubscription && checkoutCategory === productsCategories.SKINCARE)) &&
    subscribableProducts.includes(type);
  const showFrequency = subscription?.active && quantity > 0;
  const showPriceNextToQuantity = !showFrequency && !fromGiftSet;
  const showQuantity = !immutable;
  const showPriceOptions =
    (show_add_to_my_subscription || subscription?.subscribable) && quantity > 0 && !fromGiftSet;
  const productImage = getProductImage({ product, mustHaveJars: mustHaveJarsInCart });

  const shouldDisplayProductIngredients = skincareProducts.includes(slug);

  const isSkincare = checkoutCategory === productsCategories.SKINCARE;

  return (
    <Root data-testid={`product-card-${slug}`}>
      {/* Product image */}
      <Image
        alt={category === productsCategories.FORMULA ? `Bottle of ${label}` : `${label} item`}
        disabled={disabled}
        src={productImage?.src}
        width="100%"
      />

      {/* Product header (Label and capacity) */}
      <MainInfoWrapper disabled={disabled}>
        <MainInfo>
          <Typography
            color="noir"
            data-testid="product-label"
            markupName="h2"
            paragraph
            variant="h3"
          >
            {hasFirstNameInLabel && (
              <span className="hide-text-in-percy">{genitive(userFirstName)}</span>
            )}
            <span> {label}</span>
          </Typography>

          {(capacity || description_details?.sub_title) && (
            <Capacity variant="p3">{capacity || description_details?.sub_title}</Capacity>
          )}

          {type === productsSlugs.SUPPLEMENT_CORE && !isSmallMobile && (
            <Button
              aria-label="Open supplements facts modal"
              classes={{ underlinedBlack: classes.modalLabelButton }}
              data-click={`${slug}-card`}
              data-from={`${slug}-card`}
              data-testid={`${slug}-ingredients-link`}
              disabled={disabled}
              onClick={() => onModalOpen('supplements')}
              variant="underlinedBlack"
            >
              <ModalLabel markupName="span" variant="p3">
                View supplement facts
              </ModalLabel>
            </Button>
          )}

          {isSkincare && shouldDisplayProductIngredients && (
            <Button
              aria-label="Open ingredients list modal"
              classes={{ underlinedBlack: classes.modalLabelButton }}
              data-click="full-ingredients-list-popup"
              data-from={`${slug}-card`}
              data-testid={`${slug}-ingredients-link`}
              onClick={() => onModalOpen('ingredients')}
              variant="underlinedBlack"
            >
              <ModalLabel markupName="div" variant="p3">
                View ingredient list
              </ModalLabel>
            </Button>
          )}
        </MainInfo>
      </MainInfoWrapper>

      {accessoriesProducts.includes(type) ? (
        // Specific logic for accessories because the description is not formatted like haircare/skincare
        <AccessoriesDescriptionContainer disabled={disabled}>
          <Typography variant="p3">
            <Text>{first(description)}</Text>
            {description_details?.details && (
              <>
                <Spacer size={18} />
                <DescriptionDetailsContainer>
                  {description_details?.details.map(detail => (
                    <Fragment key={detail.title + detail.description}>
                      <span>{detail.title}</span>
                      <Text>{detail.description} </Text>
                      <Spacer />
                    </Fragment>
                  ))}
                </DescriptionDetailsContainer>
                <Spacer size={18} />
              </>
            )}
          </Typography>
        </AccessoriesDescriptionContainer>
      ) : (
        //  Product read-more text
        <CollapsedInfo disabled={disabled}>
          {brush.variantsContent[type] && (
            <>
              <Divider />
              <MonogramEditor
                disabled={!quantity}
                monogramsValue={monograms}
                onUpdateMonogram={value => updateMonogram({ monogram: value, item: product })}
              />
              <Divider />
            </>
          )}

          {type === productsSlugs.SUPPLEMENT_CORE && isSmallMobile && (
            <Button
              aria-label="Open supplements facts modal"
              classes={{ underlinedBlack: classes.supplementFactsButton }}
              data-click={`${slug}-card`}
              data-from={`${slug}-card`}
              data-testid="supplement-facts"
              disabled={disabled}
              onClick={() => onModalOpen('supplements')}
              variant="underlinedBlack"
            >
              <ModalLabel markupName="div" variant="p3">
                View supplement facts
              </ModalLabel>
            </Button>
          )}

          <CollapsedContent variant="p3">
            <ExtendableTextBlock
              consultationProfileCountry={consultationProfileCountry}
              dataFrom={slug}
              defaultIsOpened={defaultIsOpenedExtendableSection}
              disabled={disabled}
              isSupplements={type === productsSlugs.SUPPLEMENT_CORE}
              renderLess={() => (
                <>
                  {productType === productsSlugs.BRUSH && <span>About your {label} — </span>}
                  <Text>{first(description)}</Text>
                </>
              )}
              renderMore={() => (
                <>
                  <span>
                    {productType === productsSlugs.BRUSH && <span>About your {label} — </span>}
                    <Text>{description}</Text>
                    <Spacer size={18} />
                  </span>

                  {checkoutCategory === productsCategories.SKINCARE &&
                    clinicalClaimsContent?.[slug] && (
                      <>
                        <Button
                          aria-label="Open clinical claims modal"
                          classes={{ underlinedBlack: classes.modalLabelButton }}
                          data-click="clinical-claims-popup"
                          data-from={`${slug}-card`}
                          data-testid={`clinical-claims-button-${slug}`}
                          onClick={() => onModalOpen('clinical')}
                          variant="underlinedBlack"
                        >
                          <ModalLabel markupName="span" variant="p3">
                            Clinically tested
                          </ModalLabel>
                        </Button>{' '}
                        —<Text>{clinicalClaimsContent[slug].numbers}</Text>
                        <Spacer size={18} />
                      </>
                    )}

                  {checkoutCategory === productsCategories.HAIRCARE &&
                    clinicalClaimsContent?.[slug] && (
                      <>
                        <Button
                          aria-label="Open clinical claims modal"
                          classes={{ underlinedBlack: classes.modalLabelButton }}
                          data-click="clinical-claims-popup"
                          data-from={`${slug}-card`}
                          data-testid={`clinical-claims-button-${slug}`}
                          onClick={() => onModalOpen('clinical')}
                          variant="underlinedBlack"
                        >
                          <ModalLabel markupName="span" variant="p3">
                            {clinicalClaimsContent[slug].header}
                          </ModalLabel>
                        </Button>
                        {' - '}
                        <Text>{clinicalClaimsContent[slug].numbers}</Text>
                        <Spacer size={18} />
                      </>
                    )}

                  <FragranceDescription
                    descriptionColor="light"
                    product={product}
                    spacerAfter={18}
                  />

                  {ingredients && (
                    <>
                      <span>Key active ingredients — </span>
                      <Text>
                        {ingredients
                          .map(({ name }) => name)
                          .splice(0, 3)
                          .join(', ')}
                      </Text>
                      <Spacer size={18} />
                    </>
                  )}
                  {productType === productsSlugs.BRUSH ? (
                    <>
                      <span>Your instructions — </span>
                      <Text>{routine}</Text>
                    </>
                  ) : (
                    <>
                      <span>Instructions — </span>
                      <Text>{routine}</Text>
                      <Spacer size={18} />
                    </>
                  )}
                  {productType === productsCategories.SKINCARE && (
                    <>
                      <span>Delivery frequency — </span>
                      <Text>
                        To support a daily skincare routine, we recommend deliveries every 2 months.
                      </Text>
                    </>
                  )}
                </>
              )}
            />
          </CollapsedContent>
        </CollapsedInfo>
      )}

      {/* Product quantity selector and add to subscription list */}
      <PurchaseDetails disabled={disabled}>
        {/* Quantity selector  */}
        <Selectors>
          {showQuantity && (
            <QuantitySelectorWrapper>
              <QuantitySelector
                disabled={disabled}
                maxQuantity={maxQuantity}
                minQuantity={minQuantity}
                onSetQuantityProduct={newQuantity =>
                  quantity === 0 && newQuantity === 1
                    ? addToCartAndDefaultToSubscribeWhenPossible(product)
                    : updateItemQuantity(product, newQuantity)
                }
                quantity={quantity}
                slug={slug}
              />
            </QuantitySelectorWrapper>
          )}

          {showPriceNextToQuantity && (
            <PriceNextToQuantity
              align="right"
              className={classes.priceNearSelector}
              color="noir"
              variant="p3"
            >
              {!subscription?.price
                ? formatPriceWithCurrency({ price: price.toFixed(2), currency: currencyFromCart })
                : `From ${formatPriceWithCurrency({
                    price: subscription?.price?.toFixed(2),
                    currency: currencyFromCart,
                  })}`}
            </PriceNextToQuantity>
          )}

          {showFrequency && (
            <FrequencySelector>
              <SelectField
                aria-label="Choose frequency"
                data-click="frequency"
                data-from={`${slug}-card`}
                data-testid={`${slug}-frequency-selector`}
                id="frequency-selector"
                list={map(
                  value => ({
                    value,
                    label: getFrequencyLabelForValue({
                      value,
                      category: checkoutCategory,
                      skincareRecommendedValue: subscription?.recommended_frequency,
                      isSupplements: type === productsSlugs.SUPPLEMENT_CORE,
                    }),
                  }),
                  subscription?.frequency_options
                )}
                onChange={e =>
                  updateItemSubscription(product, {
                    active: true,
                    frequency: Number(e.target.value),
                  })
                }
                value={subscription?.frequency}
              />
            </FrequencySelector>
          )}
        </Selectors>

        {/* Buy product or add it to subscription */}
        {showPriceOptions && (
          <>
            {/* Buy product with or without subscription */}
            {enableSubscriptionAtCart && (
              <>
                <Divider />

                <PriceWrapper>
                  <PriceSelector>
                    <RadioButton
                      dataClick="subscribe"
                      dataFrom={`${slug}-card`}
                      onClick={() =>
                        subscription?.active
                          ? noop()
                          : updateItemSubscription(product, {
                              active: true,
                              // In some high concurrency cases the frequency is not set so we default to first available option
                              frequency:
                                subscription?.frequency || first(subscription?.frequency_options),
                            })
                      }
                      selected={Boolean(subscription?.active)}
                      testId="subscribe-radio-button"
                    >
                      <SubscriptionDiscountWrapper
                        align="left"
                        color="noir"
                        markupName="div"
                        variant="p3"
                      >
                        <div>Subscribe & save </div>

                        <SubscriptionDiscount>
                          <span>{subscription?.discount}% </span>

                          <QuestionMarkButton
                            aria-label="Open membership modal"
                            data-click="subscription-modal"
                            data-from={`${slug}-card`}
                            data-testid="subscription-modal-opener"
                            onClick={() => onModalOpen('subscription')}
                            onKeyPress={() => onModalOpen('subscription')}
                            role="button"
                            tabIndex={0}
                          >
                            <MembershipQuestionMark
                              style={{ '--fill-color': theme.colors.highlight[200] }}
                            />
                          </QuestionMarkButton>
                        </SubscriptionDiscount>
                      </SubscriptionDiscountWrapper>
                    </RadioButton>
                    &nbsp;
                  </PriceSelector>

                  <PriceText
                    align="right"
                    color="noir"
                    data-testid={`product-card-checkout-price-subscription-${slug}`}
                    variant="p3"
                  >
                    {formatPriceWithCurrency({
                      price: subscription?.price?.toFixed(2),
                      currency: currencyFromCart,
                    })}
                  </PriceText>
                </PriceWrapper>
              </>
            )}

            <Divider />

            {/* If already subscribed, buy product alone without subscription */}
            <PriceWrapper>
              <RadioButton
                dataClick="buy-once"
                dataFrom={`${slug}-card`}
                onClick={() =>
                  subscription?.active
                    ? updateItemSubscription(product, { active: false, frequency: null })
                    : noop()
                }
                selected={!subscription?.active}
                testId="buy-once-radio-button"
              >
                <PriceText align="left" color="noir" variant="p3">
                  Buy Once
                </PriceText>
              </RadioButton>
              <PriceText
                align="right"
                color="noir"
                data-testid={`product-card-checkout-price-once-${slug}`}
                variant="p3"
              >
                {formatPriceWithCurrency({ price: price.toFixed(2), currency: currencyFromCart })}
              </PriceText>
            </PriceWrapper>

            {/* Link to subcription */}
            {((hasActiveHaircareSubscription && checkoutCategory === productsCategories.HAIRCARE) ||
              (hasActiveSkincareSubscription &&
                checkoutCategory === productsCategories.SKINCARE)) && (
              <>
                <Divider />

                {/* Edit or start supplements subscription */}
                <SubscriptionLink>
                  <Typography color="noir" variant="p3">
                    {type === productsSlugs.SUPPLEMENT_CORE && (
                      <Link
                        data-click="add-to-existing-subscription"
                        data-from={`${slug}-card`}
                        data-testid="link-to-subscription"
                        to={
                          hasActiveHairSupplementsSubscription
                            ? `/account/subscription/supplements`
                            : `/account/subscription/supplements?add=${slug}`
                        }
                      >
                        {hasActiveHairSupplementsSubscription
                          ? 'or edit my supplements subscription'
                          : 'or start my supplements subscription'}
                      </Link>
                    )}

                    {/* Add product to haircare subscription */}
                    {type !== productsSlugs.SUPPLEMENT_CORE && (
                      <Link
                        data-click="add-to-existing-subscription"
                        data-from={`${slug}-card`}
                        data-testid="link-to-subscription"
                        to={`/account/subscription/${
                          checkoutCategory || productsCategories.HAIRCARE
                        }?add=${slug}`}
                      >
                        {(hasActiveHaircareSubscription &&
                          checkoutCategory === productsCategories.HAIRCARE) ||
                        (hasActiveSkincareSubscription &&
                          checkoutCategory === productsCategories.SKINCARE)
                          ? 'or add to my existing subscription'
                          : `or start my ${checkoutCategory} subscription`}
                      </Link>
                    )}
                  </Typography>
                </SubscriptionLink>

                <Divider />
              </>
            )}
          </>
        )}
      </PurchaseDetails>

      {/* Remove product button from cart */}
      {minQuantity === 0 && quantity > 0 && (
        <CloseCardButton
          aria-label="close"
          data-click="remove"
          data-from={`${slug}-card`}
          data-testid="product-card-closer"
          disabled={disabled}
          onClick={() => updateItemQuantity(product, 0)}
          type="button"
        >
          <Cross height={24} width={24} />
        </CloseCardButton>
      )}
    </Root>
  );
};

ProductCardCheckout.propTypes = forbidExtraProps({
  checkoutCategory: checkoutCategoryProp.isRequired,
  enableSubscriptionAtCart: PropTypes.bool.isRequired,
  addToCartAndDefaultToSubscribeWhenPossible: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fromGiftSet: PropTypes.bool,
  getProductImage: PropTypes.func.isRequired,
  hasActiveHaircareSubscription: PropTypes.bool.isRequired,
  hasActiveSkincareSubscription: PropTypes.bool.isRequired,
  hasActiveHairSupplementsSubscription: PropTypes.bool.isRequired,
  onModalOpen: PropTypes.func.isRequired,
  product: PropTypes.shape({
    description_details: PropTypes.shape({
      sub_title: PropTypes.string,
      details: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          description: PropTypes.string,
        })
      ),
    }),
    capacity: PropTypes.string,
    category: PropTypes.string.isRequired,
    customization: PropTypes.shape({
      monograms: PropTypes.string,
    }),
    description: PropTypes.arrayOf(PropTypes.string).isRequired,
    fragrance: PropTypes.string,
    fragrance_description: PropTypes.string,
    fragrance_smells_like: PropTypes.string,
    immutable: PropTypes.bool.isRequired,
    ingredients: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    label: PropTypes.string.isRequired,
    max_quantity: PropTypes.number.isRequired,
    min_quantity: PropTypes.number.isRequired,
    product: PropTypes.oneOf([
      'brush',
      'formula',
      productsSlugs.CANDLE,
      productsSlugs.HAIR_TOWEL_WRAP,
    ]),
    quantity: PropTypes.number.isRequired,
    routine: PropTypes.string,
    subscription: PropTypes.shape({
      active: PropTypes.bool,
      price: PropTypes.number,
      subscribable: PropTypes.bool,
      frequency_options: PropTypes.arrayOf(PropTypes.number),
      recommended_frequency: PropTypes.number,
    }),
    type: PropTypes.string.isRequired,
    unit_price: PropTypes.number.isRequired,
  }).isRequired,
  subscribableProducts: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateItemQuantity: PropTypes.func.isRequired,
  updateItemSubscription: PropTypes.func.isRequired,
  updateMonogram: PropTypes.func.isRequired,
  userFirstName: PropTypes.string,
  hasFirstNameInLabel: PropTypes.bool,
  defaultIsOpenedExtendableSection: PropTypes.bool,
  mustHaveJarsInCart: PropTypes.bool,
  currencyFromCart: PropTypes.string.isRequired,
  consultationProfileCountry: consultationCountriesProps,
});

ProductCardCheckout.defaultProps = {
  disabled: false,
  fromGiftSet: false,
  userFirstName: null,
  defaultIsOpenedExtendableSection: false,
  mustHaveJarsInCart: true,
  hasFirstNameInLabel: true,
  consultationProfileCountry: null,
};

export default connect(
  (state, checkoutCategory) => ({
    enableSubscriptionAtCart: getEnableSubscriptionAtCart(state, checkoutCategory),
    getProductImage: getProductImageForProduct(state),
    mustHaveJarsInCart: getMustHaveJarsInCart(state),
    hasActiveHaircareSubscription: userSelectors.getHasActiveHaircareSubscription(state),
    hasActiveSkincareSubscription: userSelectors.getHasActiveSkincareSubscription(state),
    hasActiveHairSupplementsSubscription:
      userSelectors.getHasActiveHairSupplementsSubscription(state),
    subscribableProducts: subscriptionSelectors.getSubscribableProducts(state),
    userFirstName: userSelectors.getUserFirstName(state),
    currencyFromCart: getCurrencyFromCart(state),
  }),
  {
    addToCartAndDefaultToSubscribeWhenPossible:
      checkoutCartActions.addToCartAndDefaultToSubscribeWhenPossible,
    updateItemQuantity: checkoutCartActions.updateItemQuantity,
    updateItemSubscription: checkoutCartActions.updateItemSubscription,
    updateMonogram: updateMonogramAction,
  }
)(ProductCardCheckout);
